import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {useSlider} from 'UI/components/action/Slider/useSlider';
import {SliderControls} from 'UI/components/action/SliderControls';

import {transient} from 'Application/configs/emotionTransientProps';

import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the properties for the `Slider` component.
 * This interface specifies the optional properties that can be passed to customize the slider's appearance and testing behavior.
 * It provides type safety and documentation for all configurable aspects of the slider component.
 */
interface ComponentProps {
    /**
     * Controls whether the slider should automatically advance to the next slide without user interaction.
     * When enabled, the slider will continuously cycle through slides at the specified autoplay speed interval.
     */
    autoplay?: boolean | null;
    /**
     * Defines the time interval in milliseconds between automatic slide transitions when autoplay is enabled.
     * This value determines how long each slide is displayed before automatically advancing to the next one.
     */
    autoplaySpeed?: number | null;
    /**
     * Optional CSS class name to apply custom styling to the slider wrapper element.
     * This allows for additional styling customization beyond the default component styles.
     */
    className?: string;
    /**
     * Specifies the spacing between individual slides in the slider container.
     * This gap value controls the horizontal distance between adjacent slide elements for visual separation.
     */
    gap?: string;
    /**
     * Enables infinite looping behavior where the slider continues from the first slide after reaching the last slide.
     * When disabled, the slider will stop at the last slide and require manual navigation to return to earlier slides.
     */
    loop?: boolean;
    /**
     * An optional object specifying responsive size breakpoints for image rendering.
     * These values influence the `sizes` attribute and optimize image delivery for different screen widths.
     */
    slideCount?: {
        /**
         * Number of slides to display on large screens (typically 1200px and above).
         */
        lg?: number;
        /**
         * Number of slides to display on medium screens (typically 768px to 1199px).
         */
        md?: number;
        /**
         * Number of slides to display on small screens (typically 576px to 767px).
         */
        sm?: number;
        /**
         * Number of slides to display on extra large screens (typically 1400px and above).
         */
        xl?: number;
        /**
         * Number of slides to display on extra small screens (typically below 576px).
         */
        xs?: number;
        /**
         * Number of slides to display on extra extra large screens (typically 1600px and above).
         */
        xxl?: number;
    };
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'Slider'
     */
    testId?: string;
}

/**
 * The `Slider` component provides a carousel/slider interface for displaying multiple items in a horizontal scrollable container.
 * This component uses Embla carousel functionality under the hood to manage the sliding behavior and navigation controls.
 * It renders children elements in a grid layout with automatic column sizing and includes navigation controls below the viewport for enhanced user interaction.
 *
 * @param props               The component props.
 * @param props.autoplay      Controls whether the slider should automatically advance to the next slide without user interaction.
 * @param props.autoplaySpeed Defines the time interval in milliseconds between automatic slide transitions when autoplay is enabled.
 * @param props.children      The child elements to be displayed within the slider container as individual slides.
 * @param props.className     Optional CSS class name to apply custom styling to the wrapper element.
 * @param props.gap           Specifies the spacing between individual slides in the slider container.
 * @param props.loop          Enables infinite looping behavior where the slider continues from the first slide after reaching the last slide.
 * @param props.slideCount    An optional object specifying responsive size breakpoints for determining how many slides to show at different screen sizes.
 * @param props.testId        A unique identifier for testing purposes, used to locate the component during automated tests.
 * @returns A React element representing the `Slider` component with carousel functionality and navigation controls.
 *
 * @example
 * ```tsx
 * <Slider
 *     autoplay={true}
 *     autoplaySpeed={3000}
 *     loop={true}
 *     testId="product-slider"
 *     className="my-slider"
 * >
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 *   <div>Slide 3</div>
 * </Slider>
 * ```
 */
const Slider = observer(({
    autoplay,
    autoplaySpeed,
    children,
    className,
    gap = spacing(6),
    loop,
    slideCount,
    testId = 'Slider'
}: WithChildren<ComponentProps>) => {
    const {
        containerCss,
        emblaApi,
        emblaRef
    } = useSlider(autoplay, autoplaySpeed, loop, slideCount);

    return (
        <Wrapper $loop={loop} className={className} data-cy={testId}>
            <Viewport ref={emblaRef}>
                <Container $breakpoints={containerCss} $gap={gap} $loop={loop}>
                    {children}
                </Container>
            </Viewport>
            <Spacer y={18} />
            <SliderControls empblaApi={emblaApi} />
        </Wrapper>
    );
});

Slider.displayName = 'Slider';

export {Slider};

interface WrapperProps {
    $loop?: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    margin: auto;
    max-width: ${({$loop}) => ($loop ? '119.6rem' : '100%')};
    width: 100%;
`;

const Viewport = styled.div`
    overflow: hidden;
    width: 100%;
`;

interface ContainerProps {
    $breakpoints: string;
    $gap: string;
    $loop?: boolean;
}

const Container = styled('div', transient)<ContainerProps>`
    column-gap: ${({$gap}) => $gap};
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: auto auto auto auto auto auto auto;

    ${({$breakpoints}) => $breakpoints}
`;